<template>
  <section
    v-if="banners.length"
    ref="bannerRef"
    class="card-payment"
    :style="bannerStyle"
  >
    <DesktopSwipe
      ref="swipeRef"
      class="card-swipe"
      :show-indicators="false"
      :height="bannerHeight || undefined"
      @change="onBannerChange"
    >
      <van-swipe-item
        v-for="(banner, index) in banners"
        :key="banner.id || banner.image || index"
      >
        <button
          class="card-swipe__item"
          type="button"
          :class="{ 'card-swipe__item--gap': banners.length > 1 }"
          @click="openBanner(banner)"
        >
          <app-image
            class="card-swipe__image"
            :src="banner.image"
            :lazy-load="false"
            alt=""
          />
          <span class="card-swipe__content">
            <span
              v-if="banner.title"
              class="card-swipe__title"
              :style="{ color: resolveBannerColor(banner.titleColor) }"
            >
              {{ banner.title }}
            </span>
            <div
              v-if="banner.description && isHtmlContent(banner.description)"
              class="card-swipe__description"
              :style="{ color: resolveBannerColor(banner.descriptionColor) }"
              v-html="banner.description"
            />
            <span
              v-else-if="banner.description"
              class="card-swipe__description"
              :style="{ color: resolveBannerColor(banner.descriptionColor) }"
              >{{ banner.description }}</span
            >
          </span>
        </button>
      </van-swipe-item>
    </DesktopSwipe>

    <div v-if="banners.length > 1" class="card-swipe__indicators">
      <span
        v-for="(_, index) in banners"
        :key="index"
        :class="{ active: activeIndex === index }"
        class="card-swipe__indicator"
        @click="selectBanner(index)"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { indexBanner } from "@/api/common";
import DesktopSwipe from "@/components/DesktopSwipe.vue";
import { useWebModalStore } from "@/stores/web-modal";

const { locale } = useI18n();
const router = useRouter();
const webModalStore = useWebModalStore();
const banners = ref([]);
const activeIndex = ref(0);
const bannerHeight = ref(0);
const bannerRef = ref(null);
const swipeRef = ref(null);
let autoplayTimer = 0;
let resizeObserver = null;

const bannerStyle = computed(() =>
  bannerHeight.value ? { height: `${bannerHeight.value}px` } : {},
);

function resolveBannerColor(value, fallback = "#ffffff") {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  if (raw.startsWith("#") || raw.startsWith("rgb")) return raw;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return fallback;

  const color = parsed >>> 0;
  const alpha = (color >>> 24) & 0xff;
  const red = (color >>> 16) & 0xff;
  const green = (color >>> 8) & 0xff;
  const blue = color & 0xff;
  return alpha === 0xff
    ? `rgb(${red}, ${green}, ${blue})`
    : `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
}

function isHtmlContent(value) {
  return /<\/?[a-z][^>]*>/i.test(String(value ?? "").trim());
}

function resolveBannerLanguage() {
  return String(locale.value || "zh-CN").replace(/-/g, "_");
}

function appendBannerLanguage(url) {
  return `${url}${resolveBannerLanguage()}.html`;
}

function selectBanner(index) {
  swipeRef.value?.swipeTo?.(index);
}

function onBannerChange(index) {
  activeIndex.value = index;
  startAutoplay();
}

function openBanner(banner) {
  const url = String(banner?.url || "").trim();
  if (!url) return;

  if (Number(banner?.type) === 2) {
    void router.push(url);
    return;
  }

  if (Number(banner?.type) === 3) {
    webModalStore.open({
      url: appendBannerLanguage(url),
      title: banner?.title,
      navBackgroundColor: "#fff",
    });
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

function stopAutoplay() {
  if (autoplayTimer) {
    window.clearInterval(autoplayTimer);
    autoplayTimer = 0;
  }
}

function startAutoplay() {
  stopAutoplay();
  if (banners.value.length <= 1) return;

  autoplayTimer = window.setInterval(() => {
    const nextIndex =
      activeIndex.value >= banners.value.length - 1 ? 0 : activeIndex.value + 1;
    swipeRef.value?.swipeTo?.(nextIndex);
  }, 4000);
}

function updateBannerHeight() {
  const width = bannerRef.value?.getBoundingClientRect?.().width || 0;
  if (!width) return;

  bannerHeight.value = Math.round((width * 300) / 1029);
}

function observeBannerResize() {
  if (!window.ResizeObserver || !bannerRef.value) return;

  resizeObserver?.disconnect?.();
  resizeObserver = new ResizeObserver(updateBannerHeight);
  resizeObserver.observe(bannerRef.value);
}

async function loadBanners() {
  stopAutoplay();
  try {
    const result = await indexBanner({ meta: { showLoading: false } });
    banners.value = Array.isArray(result) ? result : [];
    activeIndex.value = 0;
    await nextTick();
    observeBannerResize();
    updateBannerHeight();
    startAutoplay();
  } catch (error) {
    banners.value = [];
    activeIndex.value = 0;
    bannerHeight.value = 0;
    resizeObserver?.disconnect?.();
    resizeObserver = null;
  }
}

defineExpose({
  reload: loadBanners,
});

onMounted(() => {
  void loadBanners();
});

onUnmounted(() => {
  stopAutoplay();
  resizeObserver?.disconnect?.();
  resizeObserver = null;
});
</script>

<style scoped lang="scss">
.card-payment {
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  aspect-ratio: 1029 / 300;
  overflow: hidden;
  contain: layout paint;

  .card-swipe {
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  :deep(.desktop-swipe),
  .card-swipe,
  :deep(.van-swipe) {
    width: 100%;
    height: 100%;
  }

  :deep(.van-swipe__track),
  :deep(.van-swipe-item) {
    height: 100%;
    min-width: 0;
  }

  .card-swipe__item {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 0 16px;
    border: 0;
    background: transparent;
    border-radius: 12px;
    text-align: left;
    line-height: normal;
  }

  .card-swipe__item--gap {
    width: calc(100% - 5px);
  }

  .card-swipe__image {
    position: absolute;
    inset: 0;
    z-index: 0;
    display: block;
    width: 100%;
    height: 100%;
    max-width: none;
    border-radius: inherit;
  }

  .card-swipe__image :deep(.van-image__img) {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: fill;
  }

  .card-swipe__content {
    position: absolute;
    z-index: 1;
    display: flex;
    left: 20px;
    bottom: 0;
    height: 100%;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .card-swipe__title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.35;
  }

  .card-swipe__description {
    font-size: 12px;
    line-height: 1.35;

    :deep(p) {
      margin: 0;
    }
  }

  .card-swipe__indicators {
    position: absolute;
    right: 0;
    bottom: 10px;
    left: 0;
    z-index: 2;
    display: flex;
    justify-content: center;
    pointer-events: auto;
  }

  .card-swipe__indicator {
    width: 8px;
    height: 8px;
    margin: 0 4px;
    border-radius: 20px;
    background: #ffffff;
  }

  .card-swipe__indicator.active {
    background: var(--wp-color-theme);
  }
}
</style>
